import { AppConsts } from '@shared/AppConsts';
import { Component, Injector, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ChartOfAccountsServiceProxy, ChartOfAccountDto } from '@shared/service-proxies/service-proxies';
import { NotifyService } from 'abp-ng2-module';
import { AppComponentBase } from '@shared/common/app-component-base';
import { TokenAuthServiceProxy } from '@shared/service-proxies/service-proxies';

import { appModuleAnimation } from '@shared/animations/routerTransition';
import { Table, TableModule } from 'primeng/table';
import { Paginator, PaginatorModule } from 'primeng/paginator';
import { LazyLoadEvent, PrimeTemplate } from 'primeng/api';
import { FileDownloadService } from '@shared/utils/file-download.service';
import { filter as _filter } from 'lodash-es';
import { DateTime } from 'luxon';

import { DateTimeService } from '@app/shared/common/timing/date-time.service';

import { HttpClient } from '@angular/common/http';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { finalize } from 'rxjs';

import { ExcelColumnSelectionModalComponent } from '@app/shared/common/excel-column-selection/excel-column-selection-modal';

@Component({
    templateUrl: './chartOfAccounts.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: [appModuleAnimation()],
    standalone: false,
})
export class ChartOfAccountsComponent extends AppComponentBase {
    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    @ViewChild('ExcelFileUpload', { static: false }) excelFileUpload: FileUpload;

    @ViewChild('excelColumnSelectionModal', { static: true })
    excelColumnSelectionModal: ExcelColumnSelectionModalComponent;

    advancedFiltersAreShown = false;
    filterText = '';

    maxCreditFilter: number;
    maxCreditFilterEmpty: number;
    minCreditFilter: number;
    minCreditFilterEmpty: number;
    accountGroupNameFilter = '';
    accountSubGroupNameFilter = '';

    uploadUrl: string;

    constructor(
        injector: Injector,
        private _chartOfAccountsServiceProxy: ChartOfAccountsServiceProxy,
        private _notifyService: NotifyService,
        private _tokenAuth: TokenAuthServiceProxy,
        private _activatedRoute: ActivatedRoute,
        private _fileDownloadService: FileDownloadService,
        private _router: Router,
        private _dateTimeService: DateTimeService,
        private _httpClient: HttpClient,
    ) {
        super(injector);

        this.uploadUrl = AppConsts.remoteServiceBaseUrl + '/ChartOfAccounts/ImportFromExcel';
    }

    getChartOfAccounts(event?: LazyLoadEvent) {
        if (this.primengTableHelper.shouldResetPaging(event)) {
            this.paginator.changePage(0);
            if (this.primengTableHelper.records && this.primengTableHelper.records.length > 0) {
                return;
            }
        }

        this.primengTableHelper.showLoadingIndicator();

        this._chartOfAccountsServiceProxy
            .getAll(
                this.filterText,
                this.maxCreditFilter == null ? this.maxCreditFilterEmpty : this.maxCreditFilter,
                this.minCreditFilter == null ? this.minCreditFilterEmpty : this.minCreditFilter,
                this.accountGroupNameFilter,
                this.accountSubGroupNameFilter,
                this.primengTableHelper.getSorting(this.dataTable),
                this.primengTableHelper.getSkipCount(this.paginator, event),
                this.primengTableHelper.getMaxResultCount(this.paginator, event),
            )
            .subscribe((result) => {
                this.primengTableHelper.totalRecordsCount = result.totalCount;
                this.primengTableHelper.records = result.items;
                this.primengTableHelper.hideLoadingIndicator();
            });
    }

    reloadPage(): void {
        this.paginator.changePage(this.paginator.getPage());
    }

    createChartOfAccount(): void {
        this._router.navigate(['/app/account/chartOfAccounts/createOrEdit']);
    }

    deleteChartOfAccount(chartOfAccount: ChartOfAccountDto): void {
        this.message.confirm('', this.l('AreYouSure'), (isConfirmed) => {
            if (isConfirmed) {
                this._chartOfAccountsServiceProxy.delete(chartOfAccount.id).subscribe(() => {
                    this.reloadPage();
                    this.notify.success(this.l('SuccessfullyDeleted'));
                });
            }
        });
    }

    exportToExcel($event): void {
        this._chartOfAccountsServiceProxy
            .getChartOfAccountsToExcel(
                this.filterText,
                $event,
                this.maxCreditFilter == null ? this.maxCreditFilterEmpty : this.maxCreditFilter,
                this.minCreditFilter == null ? this.minCreditFilterEmpty : this.minCreditFilter,
                this.accountGroupNameFilter,
                undefined,
                this.accountSubGroupNameFilter,
                undefined,
            )
            .subscribe((result) => {
                this._fileDownloadService.downloadTempFile(result);
            });
    }

    uploadExcel(data: { files: File }): void {
        const formData: FormData = new FormData();
        const file = data.files[0];
        formData.append('file', file, file.name);

        this._httpClient
            .post<any>(this.uploadUrl, formData)
            .pipe(finalize(() => this.excelFileUpload.clear()))
            .subscribe((response) => {
                if (response.success) {
                    this.notify.success(this.l('ImportChartOfAccountsProcessStart'));
                } else if (response.error != null) {
                    this.notify.error(this.l('ImportChartOfAccountsUploadFailed'));
                }
            });
    }

    onUploadExcelError(): void {
        this.notify.error(this.l('ImportChartOfAccountsUploadFailed'));
    }

    resetFilters(): void {
        this.filterText = '';
        this.maxCreditFilter = this.maxCreditFilterEmpty;
        this.minCreditFilter = this.maxCreditFilterEmpty;
        this.accountGroupNameFilter = '';
        this.accountSubGroupNameFilter = '';

        this.getChartOfAccounts();
    }

    showExcelColumnSelectionModal() {
        this._chartOfAccountsServiceProxy.getChartOfAccountExcelColumnsToExcel().subscribe((result) => {
            this.excelColumnSelectionModal.show(result);
        });
    }
}
